import { useCallback, useMemo, useState } from "react";
import amenities_info_image from "../../../assets-user/post-property/amenities_info_image.png";
import { AMENITIES, getIcon } from "../../../utils/amenities";
import { showError } from "../../../utils/toastUtils";
import { SubtleWrapper } from "../../admin/common/form-elements";
import BackHeader from "./BackHeader";
import RightSideInfoCard from "./RightSideInfoCard";

const categorizeAmenities = (items) => {
  const categories = {
    lifestyle: [],
    safety: [],
    property_features: [],
    commercial: [],
    land: [],
    essential: [],
  };

  items.forEach((a) => {
    if (
      [
        "swimming_pool",
        "gymnasium",
        "yoga_meditation_area",
        "indoor_games",
        "party_lawn",
        "jogging_track",
        "children_play_area",
        "clubhouse",
      ].includes(a.key)
    ) {
      categories.lifestyle.push(a);
    } else if (
      [
        "security_24x7",
        "cctv_surveillance",
        "firefighting_equipment",
        "intercom",
      ].includes(a.key)
    ) {
      categories.safety.push(a);
    } else if (
      [
        "car_parking",
        "power_backup",
        "rainwater_harvesting",
        "sewage_treatment_plant",
      ].includes(a.key)
    ) {
      categories.property_features.push(a);
    } else if (
      [
        "high_speed_lift",
        "conference_room",
        "cafeteria",
        "internet_wifi",
        "visitors_parking",
        "maintenance_staff",
      ].includes(a.key)
    ) {
      categories.commercial.push(a);
    } else if (
      [
        "gated_community",
        "paved_compound",
        "internal_street_lights",
        "water_supply_24hr",
        "electricity_connection",
        "security_cabin",
        "boundary_wall",
      ].includes(a.key)
    ) {
      categories.land.push(a);
    } else {
      categories.essential.push(a);
    }
  });

  return categories;
};

const Tab4AmenitiesSection = ({
  formData,
  setFormData,
  onSubmit,
  onBack,
  isSubmitting,
}) => {
  const selected = formData.amenities || [];

  useState(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  
  const toggleAmenity = useCallback(
    (item) => {
      setFormData((prev) => {
        const exists = prev.amenities?.some((a) => a.key === item.key);

        return {
          ...prev,
          amenities: exists
            ? prev.amenities.filter((a) => a.key !== item.key)
            : [...prev.amenities, { key: item.key, label: item.label }],
        };
      });
    },
    [setFormData]
  );

  const categorized = useMemo(() => categorizeAmenities(AMENITIES), []);

  const handleContinue = () => {
    if (!formData.amenities || formData.amenities.length === 0) {
      showError("Please select at least one amenity");
      return;
    }
    onSubmit();
  };

  const AmenityChip = ({ item }) => {
    const iconUrl = getIcon(item.key);
    const isActive = selected.some((a) => a.key === item.key);

    return (
      <button
        onClick={() => toggleAmenity(item)}
        className={`
          px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full border transition-all text-xs sm:text-sm lg:text-base flex items-center gap-2 active:scale-[0.98]
          ${
            isActive
              ? "bg-primary/10 text-primary border-primary"
              : "border-gray-300 text-gray-600"
          }
        `}
      >
        {iconUrl && (
          <img src={iconUrl} alt="icon" className="w-4 h-4  sm:w-5   sm:h-5" />
        )}
        {item.label}
      </button>
    );
  };

  return (
    <div className="flex flex-col xl:flex-row relative w-full no-scrollbar px-3 sm:px-4 md:px-6 lg:px-10 ">
      {/* LEFT SECTION */}
      <div className="xl:w-[70%]">
        {/* Heading */}
        <BackHeader title="Amenities & Facilities" onBack={onBack} />

        <div className="max-w-3xl space-y-3 sm:space-y-4 mx-auto">
          {/* LIFESTYLE */}
          <SubtleWrapper title="Lifestyle & Recreation">
            <div className="flex flex-wrap gap-3">
              {categorized.lifestyle.map((a) => (
                <AmenityChip key={a.key} item={a} />
              ))}
            </div>
          </SubtleWrapper>

          {/* SAFETY */}
          <SubtleWrapper title="Safety & Security">
            <div className="flex flex-wrap gap-3">
              {categorized.safety.map((a) => (
                <AmenityChip key={a.key} item={a} />
              ))}
            </div>
          </SubtleWrapper>

          {/* PROPERTY FEATURES */}
          <SubtleWrapper title="Property Features">
            <div className="flex flex-wrap gap-3">
              {categorized.property_features.map((a) => (
                <AmenityChip key={a.key} item={a} />
              ))}
            </div>
          </SubtleWrapper>

          {/* COMMERCIAL FACILITIES */}
          <SubtleWrapper title="Commercial Facilities">
            <div className="flex flex-wrap gap-3">
              {categorized.commercial.map((a) => (
                <AmenityChip key={a.key} item={a} />
              ))}
            </div>
          </SubtleWrapper>

          {/* LAND FACILITIES */}
          <SubtleWrapper title="Land / Plot Facilities">
            <div className="flex flex-wrap gap-3">
              {categorized.land.map((a) => (
                <AmenityChip key={a.key} item={a} />
              ))}
            </div>
          </SubtleWrapper>

          {/* ESSENTIAL */}
          {categorized.essential.length > 0 && (
            <SubtleWrapper title="Essential Services">
              <div className="flex flex-wrap gap-3">
                {categorized.essential.map((a) => (
                  <AmenityChip key={a.key} item={a} />
                ))}
              </div>
            </SubtleWrapper>
          )}

          {/* Continue Button */}
          <div className="flex gap-3 my-8  p-4 md:pb-12">
            <button
              className="flex-1 px-6 py-3 border-2 flex items-center justify-center border-public-highlight text-public-highlight rounded-full font-medium hover:bg-public-highlight/5 transition-all duration-200"
              onClick={handleContinue}
            >
              {isSubmitting ? (
                <div className="w-5 h-5 border-2 border-public-highlight border-t-transparent rounded-full animate-spin"></div>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Right Side Info Block */}
      <div className="xl:w-[30%] mb-10 xl:mb-0 xl:min-h-screen bg-gray-50 sm:px-4  ">
        <div className="xl:sticky top-28">
          <RightSideInfoCard
            image={amenities_info_image}
            title="Why Amenities Matter?"
            description="Adding amenities makes your property more attractive, enhances value, builds buyer trust, and helps your listing stand out from the rest."
          />
        </div>
      </div>
    </div>
  );
};

export default Tab4AmenitiesSection;
